import {
  contrastRatio,
  hexToRgb,
  liftToContrast,
  mix,
  type PaletteProps
} from 'pixelize-design-library';

const AA_NORMAL_TEXT = 4.5;

import type { RiskLevel } from '@shared/property/status';

/** The library types this inline on `SemanticColors` but does not re-export the union. */
export type SemanticTone = 'success' | 'error' | 'warning' | 'info';

/**
 * Named semantic reads, so a component never spells a rung — or a hex — itself.
 * A literal colour is stuck in light mode forever; a raw rung ties the app to one
 * brand's ramp. Every helper here returns a palette token.
 */

/**
 * A raised surface: card, modal, panel, popover, toast. Never `colors.white`, which is a
 * *foreground* token and stays white in dark mode.
 *
 * This is `backgroundColor.light`, not `background[50]`. They are the same `#ffffff` in light,
 * but in dark `background[50]` **is the canvas** (`#121212`) while `backgroundColor.light` is the
 * raised `#212121` — so the obvious-looking token makes every card in the app exactly the colour
 * of the page behind it. Measured, not assumed.
 */
export const surface = (colors: PaletteProps): string => colors.backgroundColor.light;

/** The page canvas behind every surface. */
export const canvas = (colors: PaletteProps): string => colors.backgroundColor.main;

/** Secondary text. Never `gray.400`, which is the border/icon rung. */
export const mutedText = (colors: PaletteProps): string => colors.textMuted;

/** Accent-coloured text on the canvas. Never `primary[500]`, which is a fill rung. */
export const accentText = (colors: PaletteProps): string => colors.accentText;

/** `accentText` is measured against the canvas; on the darker tint it is only 4.1:1. */
export const accentInk = (colors: PaletteProps): string =>
  liftToContrast(colors.accentText, accentTint(colors), AA_NORMAL_TEXT);

export const accentTint = (colors: PaletteProps): string => colors.primary[50];

/** Surface division: table rules, card edges, dividers. */
export const ruleBorder = (colors: PaletteProps): string => colors.boxborder[200];

/** Control outline: input, select, checkbox. */
export const controlBorder = (colors: PaletteProps): string => colors.boxborder[500];

/** Status text, icon or border — resolved to 4.5:1 on the canvas. */
export const statusInk = (colors: PaletteProps, tone: SemanticTone): string =>
  colors.semanticText[tone];

/** Status fill. `[500]` is a fill rung only — it is ~3.4:1 as ink, so never use it as text. */
export const statusFill = (colors: PaletteProps, tone: SemanticTone): string =>
  colors.semantic[tone][500];

// Chip background under `statusInk` — measured ≥ 5.6:1 in both modes.
export const statusTint = (colors: PaletteProps, tone: SemanticTone): string =>
  colors.semantic[tone][50];

/** Axis labels and tick text inside a chart. */
export const chartAxisInk = (colors: PaletteProps): string => colors.textMuted;

/** Risk maps onto the semantic tones so a risk badge and a status badge agree. */
export const riskTone = (level: RiskLevel): SemanticTone =>
  level === 'high' ? 'error' : level === 'medium' ? 'warning' : 'success';

/**
 * CSS custom properties for the `.risk-slider` track. The gradient lives in `index.css`
 * because a native range input's track cannot be styled from Chakra props, but its stops
 * must still follow the palette — so the component hands them in.
 */
export const riskSliderVars = (colors: PaletteProps): Record<string, string> => ({
  '--risk-low': colors.semantic.success[500],
  '--risk-medium': colors.semantic.warning[500],
  '--risk-high': colors.semantic.error[500],
  '--risk-thumb': colors.background[50],
  '--risk-thumb-shadow': colors.boxShadow.default
});

/** WCAG AA for normal-size text. Large text (18.66px bold / 24px) is 3:1, but nothing here is. */
const AA_NORMAL = 4.5;

/**
 * A frosted panel painted over a gradient, guaranteed to keep `ink` legible on it.
 *
 * `mix(stop, ink, w)` is the frosted look — but every step toward the ink also lightens the
 * panel under ink-coloured text. The login panel's hand-picked `0.16` measured **4.22:1** with
 * white ink, under AA, and looked perfectly fine to the eye. The weight is a starting point
 * that gets walked back until the text actually clears, rather than a number chosen by taste.
 */
export function inkSafeGlass(stop: string, ink: string, weight = 0.16): string {
  for (let w = weight; w > 0; w -= 0.02) {
    const tint = mix(stop, ink, w);
    if (contrastRatio(ink, tint) >= AA_NORMAL) return tint;
  }
  return stop;
}

/**
 * A dimmed ink for a subtitle on a gradient or a glass panel.
 *
 * **Never `opacity` for this.** Opacity blends the text into whatever is behind it, so the
 * contrast it actually renders at is invisible in the source: the same panel's `opacity={0.85}`
 * measured 3.51:1. This mixes toward the backdrop only as far as AA allows, and returns the
 * full ink when even the first step would not clear.
 */
export function inkSafeMuted(backdrop: string, ink: string, weight = 0.22): string {
  for (let w = weight; w > 0; w -= 0.02) {
    const dimmed = mix(ink, backdrop, w);
    if (contrastRatio(dimmed, backdrop) >= AA_NORMAL) return dimmed;
  }
  return ink;
}

/** A tinted ambient shadow for an elevated CTA — decorative, so alpha is fine (no text sits in it). */
export function glowShadow(hex: string, alpha = 0.32): string {
  const { r, g, b } = hexToRgb(hex);
  return `0 14px 34px rgba(${r}, ${g}, ${b}, ${alpha})`;
}
